/**
 * Code Protection Component for Tolbert Tactical
 * 
 * Implements client-side protection measures:
 * - Right-click context menu blocking
 * - Keyboard shortcut prevention (Ctrl+S, F12, etc.)
 * - Developer tools detection
 * - Text selection restrictions
 * - Screenshot prevention attempts
 * 
 * AI Context: Client-side protection layer to complement server-side measures
 * and deter casual copying attempts.
 */

import { useEffect, useRef } from 'react';

const CodeProtection = ({ children, protectionLevel = 'standard' }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (protectionLevel === 'none') return;

    // Prevent right-click context menu
    const preventContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Prevent keyboard shortcuts
    const preventKeyboardShortcuts = (e) => {
      const blockedKeys = [
        { key: 'F12', ctrl: false, shift: false, alt: false },
        { key: 'I', ctrl: true, shift: false, alt: false }, // Ctrl+I (inspect)
        { key: 'J', ctrl: true, shift: false, alt: false }, // Ctrl+J (console)
        { key: 'S', ctrl: true, shift: false, alt: false }, // Ctrl+S (save)
        { key: 'U', ctrl: true, shift: false, alt: false }, // Ctrl+U (view source)
        { key: 'Shift', ctrl: false, shift: true, alt: false }, // Shift key combinations
      ];

      const isBlocked = blockedKeys.some(({ key, ctrl, shift, alt }) => {
        return e.key === key && 
               e.ctrlKey === ctrl && 
               e.shiftKey === shift && 
               e.altKey === alt;
      });

      if (isBlocked) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // Developer tools detection
    let devtools = { open: false, orientation: null };
    const detectDevTools = () => {
      const threshold = 160;
      const widthThreshold = window.outerWidth - window.innerWidth > threshold;
      const heightThreshold = window.outerHeight - window.innerHeight > threshold;
      
      if (widthThreshold || heightThreshold) {
        if (!devtools.open) {
          devtools.open = true;
          devtools.orientation = widthThreshold ? 'vertical' : 'horizontal';
          
          // Show warning or redirect
          if (protectionLevel === 'strict') {
            document.body.innerHTML = `
              <div style="
                position: fixed; 
                top: 0; 
                left: 0; 
                width: 100%; 
                height: 100%; 
                background: #000; 
                color: #fff; 
                display: flex; 
                align-items: center; 
                justify-content: center; 
                font-family: Arial, sans-serif;
                z-index: 999999;
              ">
                <div style="text-align: center;">
                  <h1>⚠️ Developer Tools Detected</h1>
                  <p>This application is protected against unauthorized access.</p>
                  <p>Please close developer tools to continue.</p>
                </div>
              </div>
            `;
          } else {
            console.warn('Developer tools detected - some features may be limited');
          }
        }
      } else {
        devtools.open = false;
      }
    };

    // Prevent text selection (optional)
    const preventSelection = (e) => {
      if (protectionLevel === 'strict') {
        e.preventDefault();
        return false;
      }
    };

    // Screenshot prevention (basic)
    const preventScreenshot = (e) => {
      if (e.key === 'PrintScreen' || (e.ctrlKey && e.key === 'p')) {
        e.preventDefault();
        return false;
      }
    };

    // Disable console methods
    const disableConsole = () => {
      if (protectionLevel === 'strict') {
        const disabledMethods = ['log', 'debug', 'info', 'warn', 'error', 'dir', 'table'];
        disabledMethods.forEach(method => {
          console[method] = function() {};
        });
      }
    };

    // Apply protection measures
    const applyProtection = () => {
      document.addEventListener('contextmenu', preventContextMenu);
      document.addEventListener('keydown', preventKeyboardShortcuts);
      document.addEventListener('keydown', preventScreenshot);
      
      if (protectionLevel === 'strict') {
        document.addEventListener('selectstart', preventSelection);
        document.addEventListener('dragstart', preventSelection);
      }
      
      // Developer tools detection
      setInterval(detectDevTools, 500);
      
      // Disable console
      disableConsole();
      
      // Add CSS to prevent selection
      if (protectionLevel === 'strict') {
        const style = document.createElement('style');
        style.textContent = `
          * {
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
          }
          input, textarea {
            -webkit-user-select: text !important;
            -moz-user-select: text !important;
            -ms-user-select: text !important;
            user-select: text !important;
          }
        `;
        document.head.appendChild(style);
      }
    };

    // Initialize protection
    applyProtection();

    // Cleanup function
    return () => {
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('keydown', preventKeyboardShortcuts);
      document.removeEventListener('keydown', preventScreenshot);
      document.removeEventListener('selectstart', preventSelection);
      document.removeEventListener('dragstart', preventSelection);
    };
  }, [protectionLevel]);

  return (
    <div ref={containerRef} className="code-protection-container">
      {children}
    </div>
  );
};

export default CodeProtection;
